import { caseScheduleRepository } from '@peritasai/repository';
import { useCallback, useEffect, useRef } from 'react';
import { clearStoredStation } from '../utils/buildCartStorage';

export type UsePickingStationClaimOptions = {
  /** Called right before the release API call fires — reset any local station state here. */
  onRelease?: () => void;
};

export type UsePickingStationClaimResult = {
  claimStation: (stationId: string) => void;
  releaseClaimedStation: () => void;
};

/**
 * Tracks the picking station claimed for the current session and releases it
 * (marks it available again, clears the remembered station) whenever the
 * session ends — on graceful navigation, component unmount, or browser/tab
 * close. The unload release relies on `keepalive: true` (set in
 * updateSidehubAvailableStatus) to survive the page actually unloading.
 */
export const usePickingStationClaim = (
  options?: UsePickingStationClaimOptions
): UsePickingStationClaimResult => {
  const claimedStationIdRef = useRef<string | null>(null);
  // Tracks the in-flight "mark unavailable" request so a release that fires
  // immediately after (fast select-then-cancel/close) can't overtake it —
  // the two are independent PUTs with no ordering guarantee, so racing them
  // could leave the station stuck busy if the server processes them out of
  // order, reintroducing the failure this hook exists to prevent.
  const pendingClaimRef = useRef<Promise<void> | null>(null);
  const onReleaseRef = useRef(options?.onRelease);
  onReleaseRef.current = options?.onRelease;

  const claimStation = useCallback((stationId: string) => {
    claimedStationIdRef.current = stationId;
    pendingClaimRef.current = caseScheduleRepository
      .updateSidehubAvailableStatus(stationId, false)
      .catch((error: unknown) => {
        console.error('Failed to mark picking station as unavailable', error);
      });
  }, []);

  const releaseClaimedStation = useCallback(() => {
    const stationId = claimedStationIdRef.current;
    if (!stationId) return;
    claimedStationIdRef.current = null;
    clearStoredStation();
    onReleaseRef.current?.();

    const pendingClaim = pendingClaimRef.current;
    pendingClaimRef.current = null;
    const release = () =>
      void caseScheduleRepository
        .updateSidehubAvailableStatus(stationId, true)
        .catch((error: unknown) => {
          console.error('Failed to mark picking station as available', error);
        });

    if (pendingClaim) {
      void pendingClaim.then(release);
    } else {
      release();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', releaseClaimedStation);
    return () => {
      window.removeEventListener('beforeunload', releaseClaimedStation);
      releaseClaimedStation();
    };
  }, [releaseClaimedStation]);

  return { claimStation, releaseClaimedStation };
};
